import { haversineKm } from "./distance";
import type { DayLegMode, DayRoute, Place, RouteLeg, Stop } from "./types";

const OSRM_BASE = "https://router.project-osrm.org";
const WALK_KM_THRESHOLD = 5;
const TRANSIT_SPEED_KMH = 20;
const TRANSIT_OVERHEAD_MIN = 8;
const WALK_SPEED_KMH = 5;

export const HOTEL_ROUTE_ID = "__hotel__";

type GeocodedPlace = Required<Pick<Place, "id" | "name" | "lat" | "lon">>;
type RouteNode = { id: string; lat: number; lon: number };

export type DayRouteOptions = {
  roundtrip?: { lat: number; lon: number; name: string };
};

type OsrmTableResponse = {
  code: string;
  durations: (number | null)[][];
};

type OsrmRouteResponse = {
  code: string;
  routes: {
    distance: number;
    duration: number;
    geometry: {
      coordinates: [number, number][];
    };
  }[];
};

export async function computeDayRoute(
  stop: Stop,
  signal?: AbortSignal,
  options?: DayRouteOptions,
): Promise<DayRoute> {
  const geocoded = geocodedPlaces(stop);
  if (geocoded.length < 2) throw new Error("need_more_places");

  const { roundtrip } = options ?? {};
  const hotelNode: RouteNode | null = roundtrip
    ? { id: HOTEL_ROUTE_ID, lat: roundtrip.lat, lon: roundtrip.lon }
    : null;

  // allNodes: [hotel (optional), ...places]
  const allNodes: RouteNode[] = hotelNode
    ? [hotelNode, ...geocoded]
    : [...geocoded];

  const matrix = await buildMatrix(allNodes, signal);
  const order = twoOpt(nearestNeighbor(matrix, 0), matrix);

  // For open route: orderedPlaceIds = all places in optimized order
  // For roundtrip: order[0] = 0 (hotel), rest are places
  const orderedPlaceIds = (hotelNode ? order.slice(1) : order).map(
    (i) => allNodes[i].id,
  );

  // Build adjacent pairs for legs
  const pairs: { from: RouteNode; to: RouteNode; matrixFrom: number; matrixTo: number }[] =
    order.slice(0, -1).map((fromIdx, i) => ({
      from: allNodes[fromIdx],
      to: allNodes[order[i + 1]],
      matrixFrom: fromIdx,
      matrixTo: order[i + 1],
    }));

  // For roundtrip, append the closing hotel leg
  if (hotelNode) {
    const lastIdx = order[order.length - 1];
    pairs.push({ from: allNodes[lastIdx], to: hotelNode, matrixFrom: lastIdx, matrixTo: 0 });
  }

  const legPairs = pairs.map(({ from, to, matrixFrom, matrixTo }) => ({
    from,
    to,
    distanceKm: haversineKm(from, to),
    durationSec: matrix[matrixFrom][matrixTo],
  }));

  // Fetch road geometry for walk legs in parallel; transit legs use straight line
  const polylineResults = await Promise.allSettled(
    legPairs.map(({ from, to, distanceKm }) =>
      distanceKm <= WALK_KM_THRESHOLD
        ? fetchOsrmRoute(from, to, signal)
        : Promise.resolve<[number, number][]>([
            [from.lat, from.lon],
            [to.lat, to.lon],
          ]),
    ),
  );

  const legs: RouteLeg[] = legPairs.map(
    ({ from, to, distanceKm, durationSec }, i) => {
      const mode: DayLegMode =
        distanceKm <= WALK_KM_THRESHOLD ? "walk" : "transit";
      const durationMin =
        mode === "walk"
          ? Math.max(1, Math.round(durationSec / 60))
          : Math.round((distanceKm / TRANSIT_SPEED_KMH) * 60) +
            TRANSIT_OVERHEAD_MIN;

      const result = polylineResults[i];
      const polyline: [number, number][] =
        result.status === "fulfilled"
          ? result.value
          : [[from.lat, from.lon], [to.lat, to.lon]];

      return { fromId: from.id, toId: to.id, mode, durationMin, distanceKm, polyline };
    },
  );

  return {
    orderedPlaceIds,
    legs,
    ...(roundtrip ? { roundtripHotel: { name: roundtrip.name } } : {}),
  };
}

function geocodedPlaces(stop: Stop): GeocodedPlace[] {
  return (stop.places ?? []).filter(
    (p): p is GeocodedPlace => p.lat != null && p.lon != null,
  );
}

async function buildMatrix(
  nodes: RouteNode[],
  signal?: AbortSignal,
): Promise<number[][]> {
  try {
    const coords = nodes.map((p) => `${p.lon},${p.lat}`).join(";");
    const url = `${OSRM_BASE}/table/v1/foot/${coords}?annotations=duration`;
    const resp = await fetch(url, { signal });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = (await resp.json()) as OsrmTableResponse;
    if (data.code !== "Ok") throw new Error(data.code);
    return data.durations.map((row) =>
      row.map((v) => (v != null ? v : Number.MAX_SAFE_INTEGER)),
    );
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    return buildFallbackMatrix(nodes);
  }
}

function buildFallbackMatrix(nodes: RouteNode[]): number[][] {
  return nodes.map((from) =>
    nodes.map((to) => (haversineKm(from, to) / WALK_SPEED_KMH) * 3600),
  );
}

async function fetchOsrmRoute(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
  signal?: AbortSignal,
): Promise<[number, number][]> {
  const url =
    `${OSRM_BASE}/route/v1/foot/${from.lon},${from.lat};${to.lon},${to.lat}` +
    `?overview=full&geometries=geojson`;
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = (await resp.json()) as OsrmRouteResponse;
  if (data.code !== "Ok" || !data.routes.length) throw new Error("no route");
  return data.routes[0].geometry.coordinates.map(([lon, lat]) => [lat, lon]);
}

function nearestNeighbor(matrix: number[][], start: number): number[] {
  const n = matrix.length;
  const visited = new Set([start]);
  const order = [start];
  let cur = start;
  while (order.length < n) {
    let best = Infinity;
    let next = -1;
    for (let i = 0; i < n; i++) {
      if (!visited.has(i) && matrix[cur][i] < best) {
        best = matrix[cur][i];
        next = i;
      }
    }
    if (next === -1) break;
    visited.add(next);
    order.push(next);
    cur = next;
  }
  return order;
}

function twoOpt(order: number[], matrix: number[][]): number[] {
  let best = [...order];
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < best.length - 1; i++) {
      for (let j = i + 2; j < best.length; j++) {
        const a = best[i];
        const b = best[i + 1];
        const c = best[j];
        const d = j + 1 < best.length ? best[j + 1] : -1;
        const curr = matrix[a][b] + (d >= 0 ? matrix[c][d] : 0);
        const swap = matrix[a][c] + (d >= 0 ? matrix[b][d] : 0);
        if (swap < curr - 0.01) {
          best = [
            ...best.slice(0, i + 1),
            ...best.slice(i + 1, j + 1).reverse(),
            ...best.slice(j + 1),
          ];
          improved = true;
        }
      }
    }
  }
  return best;
}
